import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Link, Redirect } from 'react-router-dom';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withNamespaces } from 'react-i18next';
import * as Yup from 'yup';
import { Formik, Field } from 'formik';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { isMfaEnabled, isLoggedIn, recentLogout } from '../../redux/reducers/AuthReducer';
import { isLoading, errorResponse } from '../../redux/reducers/GlobalReducer';
import { login } from '../../redux/actions/AuthActions';
import { Routes as configRouter } from '../../config/Routes';
import LoginWelcome from '../../components/Simple/LoginWelcome';
import HomeLayout from '../../layouts/LoginLayout';
import { Toast, ToastContainer, Input } from '../../components/common';
import { modifyLanguage } from '../../config/i18n';
import { getLoginButtonSubmitClasses, getLoginFormClasses } from '../../utils/themeAndStyleHelpers';

class Login extends Component {
  constructor(props) {
    super(props);
    const {
      route: {
        location: { state: { from } = { from: configRouter.DASHBOARD } },
      },
    } = props;
    this.state = {
      submitted: false,
      redirectToReferrer: !!from,
    };
  }

  setSubmitted = () =>
    this.setState({
      submitted: true,
    });

  handleSubmit = (values, actions) => {
    const { login } = this.props;
    this.setSubmitted();
    login({ username: values.email, password: values.password }).then((userData) => {
      modifyLanguage(userData.language);
      actions.setSubmitting(false);
    });
  };

  renderRedirect = () => {
    const { submitted, redirectToReferrer } = this.state;
    const {
      isMfaEnabled,
      isLoggedIn,
      recentLogout,
      route: {
        location: { pathname, state: { from } = { from: configRouter.DASHBOARD } },
      },
      errorResponse,
    } = this.props;
    if (submitted && isMfaEnabled && isLoggedIn && errorResponse === null) {
      return (
        <Redirect
          to={{
            pathname: configRouter.LOGIN_MFA,
            state: {
              url: pathname,
              from: redirectToReferrer && !recentLogout ? from : pathname,
            },
          }}
        />
      );
    }
    return null;
  };

  validationSchema = () => {
    const { t } = this.props;
    return Yup.object().shape({
      email: Yup.string().email(t('validationEmail')).required(t('validationRequired')),
      password: Yup.string().required(t('validationRequired')),
    });
  };

  render() {
    const {
      t,
      isLoading,
      route: {
        location: { state: { url } = {} },
      },
    } = this.props;

    const formClasses = getLoginFormClasses();
    const buttonClasses = getLoginButtonSubmitClasses();

    return (
      <HomeLayout>
        {this.renderRedirect()}
        <LoginWelcome title={t('loginTitle')} />
        <Formik
          initialValues={{ email: '', password: '' }}
          onSubmit={(values, actions) => {
            this.handleSubmit(values, actions);
          }}
          validationSchema={this.validationSchema()}
        >
          {(props) => (
            <form className={`${formClasses}`} onSubmit={props.handleSubmit}>
              <div className="login-inputs">
                <Field
                  component={Input}
                  label={t('labelEmail')}
                  type="email"
                  id="loginEmail"
                  name="email"
                  autoComplete="off"
                  placeholder={t('placeholderEmail')}
                  tabIndex="1"
                  required
                />
              </div>
              <div className="login-inputs">
                <Field
                  component={Input}
                  label={t('labelPassword')}
                  id="loginPassword"
                  name="password"
                  autoComplete="off"
                  placeholder={t('placeholderPassword')}
                  tabIndex="2"
                  type="password"
                  withIcon
                  canToggleIcon
                  required
                />
              </div>
              <div className="forgot-password-container">
                <label className="forgot-password">
                  <Link to={configRouter.FORGOT_PASS} className="classic-link">
                    {t('forgotPassTitle')}
                  </Link>
                </label>
              </div>
              <button type="submit" className={`${buttonClasses}`} tabIndex="3" disabled={props.isSubmitting}>
                {isLoading && <FontAwesomeIcon icon="spinner" size="lg" spin className="mx-2" />}
                {t('loginName')}
              </button>
              {process.env.REACT_APP_ENV === 'prd' && (
                <div className="text-center mt-4">
                  <small className="text-small">
                    {t('labelSignUpText')}
                    <Link to={configRouter.SIGN_UP} className="mx-2 classic-link">
                      {t('labelSignUp').toUpperCase()}
                    </Link>
                  </small>
                </div>
              )}
            </form>
          )}
        </Formik>
        {/* Success messages */}
        {(url === configRouter.AUTH_MFA || url === configRouter.SIGN_IN) && (
          <ToastContainer>
            <Toast text={t('loginAccountActiveMsg')} color="success" />
          </ToastContainer>
        )}
        {url === configRouter.RESET_PASS && (
          <ToastContainer>
            <Toast text={t('loginPasswordResetMsg')} color="success" />
          </ToastContainer>
        )}
      </HomeLayout>
    );
  }
}

Login.propTypes = {
  login: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  isLoading: isLoading(state),
  errorResponse: errorResponse(state),
  isMfaEnabled: isMfaEnabled(state),
  isLoggedIn: isLoggedIn(state),
  recentLogout: recentLogout(state),
});
const mapDispatchToProps = {
  login,
};

export default compose(withNamespaces(), connect(mapStateToProps, mapDispatchToProps))(Login);
